import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type {
  CmmdRegistration,
  CommandId,
  EnvironmentId,
  ProjectId,
  ScopedProjectRef,
} from "@cmmd-forge/contracts";
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import { readEnvironmentApi } from "../../environmentApi";
import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";
import { resolveEnvironmentHttpUrl } from "../../environments/runtime";
import { readEnvironmentBearerToken } from "../../environments/runtime/cloudRuntimeCredentialAccess";
import {
  cloneGitHubRepositoryProject,
  createEmptyProjectProject,
  linkForgeProjectIdentityToCmmdProject,
} from "../../lib/addProjectSources";
import { selectProjectsAcrossEnvironments, useStore } from "../../store";
import {
  connectCloudProjectEnvironment,
  type CloudRuntimeConnectProgressEvent,
} from "../sidebar/connectCloudRuntime";
import { resolveCloudRowProjectRefForIdentity } from "../../cmmd/cmmdCheckoutResolution";
import type { SidebarPreConnectCloudMetadata } from "../sidebar/SidebarProjectItem";
import { pollUntil } from "../../lib/pollUntil";

const CLOUD_PROJECT_REF_POLL_INTERVAL_MS = 250;

interface ConnectCloudCatalogRowInput {
  readonly onProgress: (event: CloudRuntimeConnectProgressEvent) => void;
  /**
   * Skip the "a project already lives on the runtime" short-circuit and
   * re-materialize the workspace unconditionally.
   *
   * WHY THIS EXISTS
   *
   * `resolveConnectedCloudProject` adopts the resident project whose directory
   * the row's own identity names. That is an exact match, not a heuristic (both
   * server routes derive that directory from the row), but it cannot tell a
   * healthy adoption from one where the CMMD link is stale: the directory is
   * right and the link that points at it is wrong.
   *
   * Verified 2026-08-18 on CMMD project 198, whose `forge_project_links` row
   * still named a runtime destroyed the day before. Nothing in the product
   * could rebuild it — the forge-cloud import branch is register-only, and a
   * plain connect adopts the resident project and re-affirms the same link.
   *
   * So the repair stays EXPLICIT and user-initiated. The default (`false`)
   * leaves the first-thread path as it was, so a warm sprite never pays an
   * unnecessary re-clone. `materializeCloudWorkspace` is idempotent for a repo
   * project — `ensureProjectForCwd` dedupes by cwd and the clone endpoint
   * reports `existing` — so forcing it is safe to repeat.
   */
  readonly forceMaterialize?: boolean;
}

/**
 * THE lazy-provisioning boundary.
 *
 * Starting a thread on a cloud project is what creates its sprite. For a project
 * that has never been provisioned this is a cold start (repo clone included);
 * for every thread after that `ensure` returns the existing runtime and this is
 * just a reconnect. The runtime id is whatever CMMD settles on — the catalog row
 * may still say null on the very first thread.
 */
export async function connectCloudCatalogRow(
  row: CloudProjectCatalogRow,
  input: ConnectCloudCatalogRowInput,
): Promise<ScopedProjectRef> {
  // Cached project state proves identity, not liveness. Always reacquire a
  // current CMMD-signed ticket at the execution boundary.
  const { environmentId } = await connectCloudProjectEnvironment(toPreConnectCloudMetadata(row), {
    onProgress: input.onProgress,
  });

  // An explicit repair re-materializes even when the runtime already reports a
  // project, because that resident project is exactly what a broken row cannot
  // be trusted about. See `forceMaterialize`.
  if (input.forceMaterialize !== true) {
    const connectedRef = await resolveConnectedCloudProject(row, environmentId);
    if (connectedRef) {
      await persistResolvedCloudProjectLink(row, connectedRef);
      return connectedRef;
    }
  }
  return cloneAndHydrateCloudProject(row, environmentId, input);
}

/**
 * The project on the runtime that THIS row names, or null.
 *
 * The row travels the whole way down. A per-owner runtime hosts every project
 * its user has registered, so a runtime id alone names a set, not a project
 * (FOR-36358).
 */
async function resolveConnectedCloudProject(
  row: CloudProjectCatalogRow,
  environmentId: EnvironmentId,
): Promise<ScopedProjectRef | null> {
  const alreadyResolved = readCloudProjectRef(row, environmentId);
  if (alreadyResolved !== null) return alreadyResolved;
  return waitForCloudProjectRef(row, environmentId, import.meta.env.MODE === "test" ? 1 : 20);
}

function readCloudProjectRef(
  row: CloudProjectCatalogRow,
  environmentId: EnvironmentId,
): ScopedProjectRef | null {
  const resolved = resolveCloudRowProjectRefForIdentity(
    useStore.getState().environmentStateById,
    environmentId,
    {
      cmmdOrganizationId: row.cmmdOrganizationId,
      cmmdProjectId: row.cmmdProjectId,
      githubRepoFullName: row.githubRepoFullName,
      projectName: row.projectName,
      workspaceRoot: row.workspaceRoot,
    },
  );
  return resolved === null ? null : scopeProjectRef(resolved.environmentId, resolved.projectId);
}

/**
 * Materialize the project's workspace INSIDE the freshly provisioned sprite.
 *
 * This is the work import used to do eagerly. Deferring provisioning defers the
 * clone with it, so the first thread is where the repo actually lands on disk.
 * A project with a repo is cloned; a blank project gets an empty workspace
 * directory created under the same name.
 */
async function cloneAndHydrateCloudProject(
  row: CloudProjectCatalogRow,
  environmentId: EnvironmentId,
  input: ConnectCloudCatalogRowInput,
): Promise<ScopedProjectRef> {
  const api = readEnvironmentApi(environmentId);
  if (api === undefined) {
    throw new Error("Cloud project connected, but its command API was not ready.");
  }

  const repoFullName = row.githubRepoFullName;
  input.onProgress(
    createCloneProgress(
      "clone-request",
      repoFullName ? "Cloning cloud project repository" : "Creating cloud project",
      repoFullName ?? row.projectName,
    ),
  );
  const result = await materializeCloudWorkspace(row, environmentId, api);
  input.onProgress(
    createCloneProgress(
      "clone-registered",
      result.existing ? "Cloud project already registered" : "Cloud project registered",
      row.workspaceRoot ?? repoFullName ?? row.projectName,
    ),
  );

  const registeredRef = scopeProjectRef(environmentId, result.projectId as ProjectId);
  const hydratedRef = await waitForSpecificCloudProjectRef(
    registeredRef,
    import.meta.env.MODE === "test" ? 1 : 40,
  );
  if (hydratedRef === null) {
    throw new Error("Cloud project connected, but its project snapshot was not ready.");
  }
  await persistResolvedCloudProjectLink(row, hydratedRef);
  return hydratedRef;
}

async function persistResolvedCloudProjectLink(
  row: CloudProjectCatalogRow,
  projectRef: ScopedProjectRef,
): Promise<void> {
  const hydratedProject =
    useStore.getState().environmentStateById[projectRef.environmentId]?.projectById[
      projectRef.projectId
    ];
  if (!hydratedProject) {
    throw new Error("Cloud project connected, but its linked project disappeared.");
  }
  await stampRegistrationOnCheckout(row, projectRef, hydratedProject.cmmdRegistration ?? null);
  await persistCloudCatalogProjectLink({
    row,
    projectRef,
    cwd: hydratedProject.cwd,
  });
}

/**
 * Record on the Checkout which Registration it belongs to.
 *
 * Both paths above end here, so a Checkout Forge just created and one it
 * adopted are stamped the same way, and a Checkout registered before this
 * field existed gains its Registration the next time someone opens it.
 *
 * Without it the record can only be named by its directory, and on the shared
 * owner Environment a connected project reports an empty `cwd` and the row
 * reports the sprite's root, so there is no directory to compare and every
 * lookup falls back to whatever row happens to be there.
 *
 * Skipped when the record already carries this Registration, so a warm
 * reconnect writes nothing.
 */
async function stampRegistrationOnCheckout(
  row: CloudProjectCatalogRow,
  projectRef: ScopedProjectRef,
  carried: CmmdRegistration | null,
): Promise<void> {
  if (
    carried?.cmmdOrganizationId === row.cmmdOrganizationId &&
    carried?.cmmdProjectId === row.cmmdProjectId
  ) {
    return;
  }
  const api = readEnvironmentApi(projectRef.environmentId);
  if (api === undefined) {
    throw new Error("Cloud project connected, but its command API was not ready.");
  }
  await api.orchestration.dispatchCommand({
    type: "project.meta.update",
    commandId: crypto.randomUUID() as CommandId,
    projectId: projectRef.projectId,
    cmmdRegistration: {
      cmmdOrganizationId: row.cmmdOrganizationId,
      cmmdProjectId: row.cmmdProjectId,
    },
  });
}

export async function persistCloudCatalogProjectLink(input: {
  readonly row: CloudProjectCatalogRow;
  readonly projectRef: ScopedProjectRef;
  readonly cwd: string;
  readonly fetchImpl?: typeof fetch;
  readonly resolveLinkHttpUrl?: (path: string) => string;
}): Promise<void> {
  await linkForgeProjectIdentityToCmmdProject({
    environmentId: input.projectRef.environmentId,
    projectId: input.projectRef.projectId,
    cwd: input.cwd,
    cmmdOrganizationId: input.row.cmmdOrganizationId,
    cmmdProjectId: input.row.cmmdProjectId,
    cmmdProjectName: input.row.projectName,
    cmmdWorkspaceName: "Projects",
    githubRepoFullName: input.row.githubRepoFullName,
    source: "created_from_cmmd",
    ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
    resolveHttpUrl: input.resolveLinkHttpUrl ?? resolvePrimaryEnvironmentHttpUrl,
    applyPrimaryAuth: input.fetchImpl === undefined,
  });
}

/** Clone the project's repo into the sprite, or create an empty project. */
function materializeCloudWorkspace(
  row: CloudProjectCatalogRow,
  environmentId: EnvironmentId,
  api: NonNullable<ReturnType<typeof readEnvironmentApi>>,
) {
  const projectBase = {
    api,
    environmentId,
    projects: selectProjectsAcrossEnvironments(useStore.getState()),
    defaultThreadEnvMode: "worktree" as const,
    handleNewThread: async () => undefined,
    resolveHttpUrl: (pathname: string, searchParams?: Record<string, string>) =>
      resolveEnvironmentHttpUrl({
        environmentId,
        pathname,
        ...(searchParams ? { searchParams } : {}),
      }),
    fetchImpl: (request: RequestInfo | URL, init?: RequestInit) =>
      fetchCloudRuntime(request, init, environmentId),
  };
  return row.githubRepoFullName
    ? cloneGitHubRepositoryProject({
        ...projectBase,
        openThreadAfterRegister: false,
        repoFullName: row.githubRepoFullName,
      })
    : createEmptyProjectProject({ ...projectBase, name: row.projectName });
}

function createCloneProgress(
  stage: "clone-request" | "clone-registered",
  summary: string,
  detail: string,
): CloudRuntimeConnectProgressEvent {
  return { stage, summary, detail, at: new Date().toISOString() };
}

async function fetchCloudRuntime(
  request: RequestInfo | URL,
  init: RequestInit | undefined,
  environmentId: EnvironmentId,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  const bearerToken = await readEnvironmentBearerToken(environmentId);
  if (bearerToken !== null && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${bearerToken}`);
  }
  return fetch(request, { ...init, credentials: "omit", headers });
}

function waitForCloudProjectRef(
  row: CloudProjectCatalogRow,
  environmentId: EnvironmentId,
  maxAttempts: number,
): Promise<ScopedProjectRef | null> {
  return pollUntil(() => readCloudProjectRef(row, environmentId), {
    maxAttempts,
    intervalMs: CLOUD_PROJECT_REF_POLL_INTERVAL_MS,
  });
}

function waitForSpecificCloudProjectRef(
  projectRef: ScopedProjectRef,
  maxAttempts: number,
): Promise<ScopedProjectRef | null> {
  return pollUntil(
    () => {
      const project =
        useStore.getState().environmentStateById[projectRef.environmentId]?.projectById[
          projectRef.projectId
        ];
      return project !== undefined ? projectRef : null;
    },
    { maxAttempts, intervalMs: CLOUD_PROJECT_REF_POLL_INTERVAL_MS },
  );
}

function toPreConnectCloudMetadata(row: CloudProjectCatalogRow): SidebarPreConnectCloudMetadata {
  return {
    runtimeId: row.runtimeId,
    cmmdOrganizationId: row.cmmdOrganizationId,
    cmmdProjectId: row.cmmdProjectId,
    // FOR-32652. Same Environment the sidebar row would name, so the entry
    // point a user happens to take cannot change which identifier is sent.
    cmmdEnvironmentId: row.cmmdEnvironmentId,
    registeredAt: row.registeredAt,
    projectName: row.projectName,
    githubRepoFullName: row.githubRepoFullName,
    workspaceRoot: row.workspaceRoot,
    iconUrl: row.iconUrl,
    icon: row.icon,
    status: row.status,
  };
}
